// eslint-disable-next-line import-length/import-length
import { PriceFromStyled } from '@portalterreno/nextjs-client/portalterreno/components/PropertyCard';
import Visible from '@portalterreno/react-ui/Visible';
import { Col, Row } from '@portalterreno/react-ui-core/Grid';
import { useRouter } from 'next/router';
import React, { ReactNode } from 'react';

import RelatedPropertiesHeader, {
  RelatedPropertiesHeaderProps
} from './RelatedPropertiesHeader';
import RelatedPropertyCard, {
  RelatedPropertyCardProps
} from './RelatedPropertyCard';
import StyledDivDesktop from './StyledDivDesktop';
import StyledDivMobile from './StyledDivMobile';

export type RelatedPropertiesProps = Pick<
  RelatedPropertyCardProps,
  'epigraph' | 'surfaceLabel'
> &
  Pick<RelatedPropertiesHeaderProps, 'title'> & {
    relatedProperties: RelatedPropertyCardProps[];
    totalResultsPerPage: number;
    totalPages: number;
    totalResults: number;
  };

const RelatedProperties: React.FC<RelatedPropertiesProps> = ({
  epigraph,
  relatedProperties,
  surfaceLabel,
  totalResultsPerPage,
  totalPages,
  totalResults,
  title
}) => {
  const router = useRouter();

  const initialState = React.useMemo(() => {
    return {
      firstPageCard: 0,
      lastPageCard: totalResultsPerPage,
      currentPage: 1
    };
  }, [totalResultsPerPage]);

  const [state, setState] = React.useState(initialState);

  React.useEffect(() => {
    setState(initialState);
  }, [initialState, router.asPath]);

  const handleNextPage = React.useCallback(() => {
    setState((previousState) => ({
      firstPageCard: previousState.firstPageCard + totalResultsPerPage,
      lastPageCard: previousState.lastPageCard + totalResultsPerPage,
      currentPage: previousState.currentPage + 1
    }));
  }, [totalResultsPerPage]);

  const handlePrevPage = React.useCallback(() => {
    setState((previousState) => ({
      firstPageCard: previousState.firstPageCard - totalResultsPerPage,
      lastPageCard: previousState.lastPageCard - totalResultsPerPage,
      currentPage: previousState.currentPage - 1
    }));
  }, [totalResultsPerPage]);

  const limitPages = React.useMemo(() => {
    if (relatedProperties.length < totalResults) {
      return Math.ceil(relatedProperties.length / totalResultsPerPage);
    }
    return totalPages;
  }, [relatedProperties, totalResultsPerPage, totalPages, totalResults]);

  return (
    <React.Fragment>
      <Row>
        <Col>
          <RelatedPropertiesHeader
            handleNextPage={handleNextPage}
            handlePrevPage={handlePrevPage}
            currentPage={state.currentPage}
            limitPages={limitPages}
            title={title}
          />
        </Col>
      </Row>
      <Visible desktop>
        <StyledDivDesktop>
          <Row>
            {relatedProperties.map((property, index) => {
              if (index < state.firstPageCard || index >= state.lastPageCard) {
                return null;
              }

              let propertyEpigraph: ReactNode | undefined;

              if (property.epigraph) {
                propertyEpigraph = (
                  <PriceFromStyled>{property.epigraph}</PriceFromStyled>
                );
              } else {
                propertyEpigraph = epigraph;
              }

              return (
                <Col
                  lg={3}
                  key={index}
                >
                  <RelatedPropertyCard
                    chips={property.chips}
                    cityName={property.cityName}
                    communeName={property.communeName}
                    epigraph={propertyEpigraph}
                    photoCover={property.photoCover}
                    pricing={property.pricing}
                    principalCurrency={property.principalCurrency}
                    propertyLogo={property.propertyLogo}
                    surface={property.surface}
                    surfaceLabel={property.surfaceLabel || surfaceLabel}
                    surfaceUnit={property.surfaceUnit}
                    title={property.title}
                    url={property.url}
                  />
                </Col>
              );
            })}
          </Row>
        </StyledDivDesktop>
      </Visible>
      <Visible mobile>
        <Row>
          <Col>
            <StyledDivMobile>
              {relatedProperties.map((property, index) => {
                let propertyEpigraph: ReactNode | undefined;

                if (property.epigraph) {
                  propertyEpigraph = (
                    <PriceFromStyled>{property.epigraph}</PriceFromStyled>
                  );
                } else {
                  propertyEpigraph = epigraph;
                }

                return (
                  <RelatedPropertyCard
                    chips={property.chips}
                    cityName={property.cityName}
                    communeName={property.communeName}
                    epigraph={propertyEpigraph}
                    key={index}
                    photoCover={property.photoCover}
                    pricing={property.pricing}
                    principalCurrency={property.principalCurrency}
                    propertyLogo={property.propertyLogo}
                    surface={property.surface}
                    surfaceLabel={property.surfaceLabel || surfaceLabel}
                    surfaceUnit={property.surfaceUnit}
                    title={property.title}
                    url={property.url}
                  />
                );
              })}
            </StyledDivMobile>
          </Col>
        </Row>
      </Visible>
    </React.Fragment>
  );
};

export default React.memo(RelatedProperties);
